import { ResourceGroupsView } from "../../features/resourceGroups";
import { HeadingMedium, LabelSmall, ParagraphXSmall } from "baseui/typography";
import { TenantsView } from "../../features/tenants";
import {
  useDeleteSubscriptionAdminMutation,
  useGetSubscriptionAdminsQuery,
  useGetSubscriptionByIdQuery,
  useGetSubscriptionOwnerQuery,
} from "../../services/resourceGroups/subscriptionApi";
import { Button } from "baseui/button";
import { VscEdit, VscKey, VscTrash } from "react-icons/vsc";
import { useStyletron } from "baseui";
import { LoadingView } from "../../components/LoadingView";
import { CurrentSubscriptionUsage, DeleteSubscriptionDialog } from ".";
import { useState } from "react";
import { StyledLink } from "baseui/link";
import { Notification } from "baseui/notification";
import UpdateDialog from "./UpdateDialog/UpdateDialog";
import { useGetResourceGroupsQuery } from "../../services/resourceGroups/resourceGroupApi";
import { useGetTenantsQuery } from "../../services/resourceGroups/tenantApi";
import { getStripeId } from "../../services/resourceGroups/utils";
import { HorizontalStackPanel } from "../../components/Layout/StackPanel";
import SelectOwnerDialog from "./selectOwnerDialog/SelectOwnerDialog";
import { ColoredAvatar } from "../../components/ColoredAvatar/ColoredAvatar";
import { ListItem, ListItemLabel } from "baseui/list";
import { ChevronRight } from "baseui/icon";
import { Badge } from "baseui/badge";
import AddAdminDialog from "./addAdminDialog/AddAdminDialog";
import { FormattedMessage } from "react-intl";
import { getStripeUrl } from "../../utils/utils";
import SubscriptionStatusView from "./SubscriptionStatus";
import { Banner } from "baseui/banner";
import SubscriptionEvents from "../subscriptionEvents/SubscriptionEvents";
import { CreateTenantDialog, TenantAddress } from "../tenant";
import { AccessRoles, useHasRole } from "../../hooks/useHasRole";
import { ISubscription } from "@oqdo/automationapps/Memberships/Subscription";
import { IPerson } from "@oqdo/automationapps/Agents/Person";
import { CreateResourceGroupDialog } from "../resourceGroup";
import { Tab, Tabs } from "baseui/tabs-motion";
import { StyledRoot, StyledToolbar } from "./styled-components";
import { Avatar } from "baseui/avatar";

function Subscription(props: { subscriptionId: string }) {
  const { subscriptionId } = props;
  const [css, theme] = useStyletron();
  const { data, isLoading, isSuccess, error } = useGetSubscriptionByIdQuery(subscriptionId, { skip: !subscriptionId });
  const { data: owner, isSuccess: ownerLoaded } = useGetSubscriptionOwnerQuery(subscriptionId, {
    skip: !subscriptionId,
  });

  const isSa = useHasRole(AccessRoles.SuperAdmin);

  const [isOpenSelectOwner, setIsOpenSelectOwner] = useState(false);
  const [activeKey, setActiveKey] = useState<any>(0);

  const stripeId = data && getStripeId(data);

  const handleSelectOwner = () => {
    setIsOpenSelectOwner(true);
  };

  return (
    <StyledRoot>
      <LoadingView isLoading={isLoading} isSucess={isSuccess} error={error}>
        <LabelSmall marginBottom={theme.sizing.scale200} color={theme.colors.accent}>
          <FormattedMessage id="Subscription" defaultMessage={"Subscription"} />
        </LabelSmall>
        {data && (
          <>
            <ActionButtons subscription={data} />
            <HorizontalStackPanel $alignItems={"center"} $gap={theme.sizing.scale300}>
              <VscKey color={theme.colors.primary} />
              <HeadingMedium margin={0}>{data?.name}</HeadingMedium>
              <SubscriptionStatusView {...data} />
            </HorizontalStackPanel>
            <ParagraphXSmall color={theme.colors.contentTertiary}>{subscriptionId}</ParagraphXSmall>

            <Tabs
              activeKey={activeKey}
              onChange={({ activeKey }) => {
                setActiveKey(activeKey);
              }}
              activateOnFocus>
              <Tab title={<FormattedMessage id="owner" defaultMessage={"Owner"} />}>
                <>
                  {owner && (
                    <ListItem artwork={() => <Avatar name={owner.name} />}>
                      <ListItemLabel description={<TenantAddress address={owner.address} />} children={owner?.name} />
                    </ListItem>
                  )}
                  {!owner && ownerLoaded && (
                    <>
                      <Banner
                        kind="warning"
                        action={{
                          label: "Select",
                          onClick: handleSelectOwner,
                        }}>
                        No owner selected
                      </Banner>
                      {isOpenSelectOwner && (
                        <SelectOwnerDialog
                          isOpen={isOpenSelectOwner}
                          setIsOpen={setIsOpenSelectOwner}
                          subscriptionId={props.subscriptionId}
                        />
                      )}
                    </>
                  )}
                </>
              </Tab>
              <Tab title={<FormattedMessage id="administrators" defaultMessage={"Adminstrators"} />}>
                <AdminUsers subscriptionId={subscriptionId} />
              </Tab>
              <Tab title={<FormattedMessage id="Tenants" defaultMessage={"Tenants"} />}>
                <StyledToolbar>
                  <AddTenantDialog subscriptionId={subscriptionId} />
                </StyledToolbar>
                <TenantsView subscriptionId={subscriptionId} />
              </Tab>
              <Tab title={<FormattedMessage id="RessourceGroups" defaultMessage={"RessourceGroups"} />}>
                <StyledToolbar>{isSa && <AddNewResourceGroupDialog subscriptionId={subscriptionId} />}</StyledToolbar>
                <ResourceGroupsView subscriptionId={subscriptionId} />
              </Tab>
              <Tab title={"Logs"}>
                <SubscriptionEvents id={subscriptionId} />
              </Tab>

              <Tab title={<FormattedMessage id="Payment" defaultMessage={"Payment"} />}>
                <>
                  {isSa && (
                    <>
                      {stripeId && (
                        <ParagraphXSmall>
                          <StyledLink target="_blank" href={getStripeUrl(stripeId)}>
                            Stripe Subscription
                          </StyledLink>
                        </ParagraphXSmall>
                      )}

                      {!stripeId && <Notification kind="warning">No Stripe subscription!</Notification>}
                    </>
                  )}
                  <CurrentSubscriptionUsage subscription={data} />
                </>
              </Tab>
            </Tabs>
          </>
        )}
      </LoadingView>
    </StyledRoot>
  );
}

export default Subscription;

const ActionButtons = (props: { subscription: ISubscription }) => {
  const { subscription } = props;
  const [css, theme] = useStyletron();
  const [isOpen, setIsOpen] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const { data: rgsData } = useGetResourceGroupsQuery({ subscriptionId: subscription.id, page: 0, pageSize: 1000 });
  const { data: tenantsData } = useGetTenantsQuery(subscription.id);
  const isSa = useHasRole(AccessRoles.SuperAdmin);

  const rgs = rgsData?.data;
  const tenants = tenantsData?.data;
  const hasRgs = rgs && rgs.length > 0;
  const hasTs = tenants && tenants.length > 0;
  return (
    <>
      <div
        className={css({
          position: "absolute",
          top: "-" + theme.sizing.scale800,
          right: theme.sizing.scale500,
        })}>
        <Button
          onClick={() => {
            setShowUpdateDialog(true);
          }}
          title="Edit Subscription"
          size="default"
          kind="secondary"
          shape="circle">
          <VscEdit />
        </Button>
        {isSa && (
          <Button
            disabled={hasRgs || hasTs}
            onClick={() => {
              setIsOpen(true);
            }}
            title="Delete Subscription"
            size="default"
            kind="secondary"
            shape="circle">
            <VscTrash />
          </Button>
        )}
      </div>
      {isOpen && <DeleteSubscriptionDialog subscription={subscription} isOpen={isOpen} setIsOpen={setIsOpen} />}
      {showUpdateDialog && (
        <UpdateDialog isOpen={showUpdateDialog} setIsOpen={setShowUpdateDialog} subscription={subscription} />
      )}
    </>
  );
};

const AdminUsers: React.FC<{ subscriptionId: string }> = (props) => {
  const { subscriptionId } = props;
  const [css, theme] = useStyletron();
  const {
    data: admins,
    isSuccess,
    error,
    isLoading,
  } = useGetSubscriptionAdminsQuery(subscriptionId, { skip: !subscriptionId });
  const [showAddAdmin, setShowAddAdmin] = useState(false);

  const isEmpty = isSuccess && admins.length === 0;

  return (
    <>
      <StyledToolbar>
        <ParagraphXSmall margin={0}>
          <Button
            size="compact"
            shape="pill"
            onClick={() => {
              setShowAddAdmin(true);
            }}>
            <FormattedMessage id="inviteAdmin" defaultMessage={"Invite another user as administrator"} />
          </Button>
        </ParagraphXSmall>
      </StyledToolbar>

      {isSuccess && (
        <>
          {isEmpty ? (
            <Banner
              kind="warning"
              title="No administrators defined"
              action={{
                label: "Add",
                onClick: () => {
                  setShowAddAdmin(true);
                },
              }}>
              Add at least one admin
            </Banner>
          ) : (
            <ul
              className={css({
                paddingLeft: 0,
                paddingRight: 0,
                overflowY: "auto",
                maxHeight: "60vh",
              })}>
              {admins?.map((v) => <AdminUser key={v.id} user={v} subscriptionId={subscriptionId} />)}
            </ul>
          )}
        </>
      )}

      {showAddAdmin && (
        <AddAdminDialog
          subscriptionId={subscriptionId}
          isOpen={showAddAdmin}
          setIsOpen={(value) => {
            setShowAddAdmin(value);
          }}
        />
      )}
    </>
  );
};

const AdminUser = (props: { user: IPerson; subscriptionId: string }) => {
  const { user, subscriptionId } = props;
  const [del, delResult] = useDeleteSubscriptionAdminMutation();

  const [isDeleted, setIsDeleted] = useState<boolean>(false);
  const onRemove = async () => {
    await del({ subscriptionId: subscriptionId, userId: user.id });
    setIsDeleted(true);
  };

  const disabled = user.accountEnabled !== undefined && user.accountEnabled !== null ? !user.accountEnabled : false;
  const isNotActive = user.pendingInvitation;

  if (isDeleted) return <></>;
  return (
    <ListItem
      artwork={() => <ColoredAvatar username={user.displayName ?? user.mail} />}
      key={user.id}
      endEnhancer={() => (
        <HorizontalStackPanel $alignItems={"center"}>
          {isNotActive && (
            <>
              {" "}
              <ParagraphXSmall margin={0}>{"Invitation Status"}</ParagraphXSmall>{" "}
              <Badge
                hierarchy={"secondary"}
                color={"warning"}
                content={user.pendingInvitation ? "Pending Invitation" : ""}
              />
            </>
          )}
          <Button
            onClick={onRemove}
            isLoading={delResult.isLoading}
            title="Remove User"
            size="compact"
            kind="secondary"
            shape="pill">
            <VscTrash />
          </Button>
          <Button $as="a" href={`/subscriptions/${subscriptionId}/users/${user.id}`} kind="tertiary" shape="circle">
            <ChevronRight size="scale800" />
          </Button>
        </HorizontalStackPanel>
      )}>
      <ListItemLabel description={user.mail}>
        {user.displayName}{" "}
        {disabled && <Badge content={"Account disabled"} hierarchy="secondary" color="negative" />}{" "}
      </ListItemLabel>
    </ListItem>
  );
};

function AddTenantDialog({ subscriptionId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [css, theme] = useStyletron();
  return (
    <>
      <Button
        onClick={() => {
          setIsOpen(true);
        }}
        title="Add Tenant"
        size="compact"
        shape="pill"
        kind="primary">
        <FormattedMessage id="add" defaultMessage={"Add"} />
      </Button>
      <CreateTenantDialog subscriptionId={subscriptionId} setIsOpen={setIsOpen} isOpen={isOpen} />
    </>
  );
}

function AddNewResourceGroupDialog({ subscriptionId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [css, theme] = useStyletron();
  return (
    <>
      <Button
        onClick={() => {
          setIsOpen(true);
        }}
        title="Add Tenant"
        size="compact"
        shape="pill"
        kind="primary">
        <FormattedMessage id="add" defaultMessage={"Add"} />
      </Button>
      <CreateResourceGroupDialog setIsOpen={setIsOpen} isOpen={isOpen} subscriptionId={subscriptionId} />
    </>
  );
}
